import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';

import { CarrierMappingDrafts } from './CarrierMappingDrafts';
import { api, type CarrierAuditProgress, type CarrierAuditRecord } from '@/services/api';

interface CarrierAuditReviewProps {
  disabled: boolean;
  surfaceKey: string;
}

const OUTCOME_LABELS = {
  matched: 'Matched',
  renamed: 'Renamed',
  new: 'New',
  missing: 'Missing',
  conflict: 'Conflict',
  ignored: 'Ignored',
} as const;

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'The Carrier audit review could not be loaded.';
}

function recordName(record: CarrierAuditRecord): string {
  return record.providerName ?? record.baselineProviderName ?? `Record ${record.providerRecordId}`;
}

export function CarrierAuditReview({ disabled, surfaceKey }: CarrierAuditReviewProps) {
  const [audit, setAudit] = useState<CarrierAuditProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void api.carrierInitialization.getCarrierAudit()
      .then((result) => {
        if (!active) return;
        setAudit(result);
        setError(null);
      })
      .catch((loadError) => {
        if (active) setError(errorMessage(loadError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const actionableRecords = useMemo(() => audit?.records.filter((record) => (
    record.outcome === 'new'
    || record.outcome === 'renamed'
    || record.outcome === 'missing'
    || record.outcome === 'conflict'
  )) ?? [], [audit]);
  const actionableCount = audit
    ? audit.counts.new + audit.counts.renamed + audit.counts.missing + audit.counts.conflict
    : 0;
  const reviewPrepared = audit?.reviewPreparedAt !== null && audit?.reviewPreparedAt !== undefined;

  const prepareReview = async () => {
    if (!audit || preparing || disabled || actionableCount === 0) return;
    setPreparing(true);
    setError(null);
    try {
      setAudit(await api.carrierInitialization.prepareCarrierAuditReview(audit.runId));
    } catch (prepareError) {
      setError(errorMessage(prepareError));
    } finally {
      setPreparing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-surface-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Carrier audit…
      </div>
    );
  }

  if (!audit) {
    return (
      <div role="alert" className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        {error ?? 'No saved Carrier audit was found.'}
      </div>
    );
  }

  return (
    <section aria-label="Carrier audit review" className="w-full">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[0.65rem] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">Carrier audit</p>
          <h3 className="text-lg font-semibold text-surface-950 dark:text-white">Review Carrier changes</h3>
          <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
            {audit.observedAccounts} Inbound trunks · {audit.observedVendors} Outbound trunks
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300">
          <ShieldCheck className="h-4 w-4" /> Read completed
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
        {(Object.keys(OUTCOME_LABELS) as Array<keyof typeof OUTCOME_LABELS>).map((outcome) => (
          <div key={outcome} className="rounded-lg border border-surface-200 bg-surface-50 px-2 py-2 dark:border-surface-700 dark:bg-surface-950">
            <p className="text-[0.62rem] font-semibold uppercase tracking-wide text-surface-400">{OUTCOME_LABELS[outcome]}</p>
            <p className="mt-0.5 text-lg font-semibold text-surface-900 dark:text-white">{audit.counts[outcome]}</p>
          </div>
        ))}
      </div>

      {actionableRecords.length > 0 && (
        <div className="mt-4 max-h-64 overflow-y-auto rounded-lg border border-surface-200 dark:border-surface-700">
          {actionableRecords.map((record) => (
            <div key={`${record.recordType}:${record.providerRecordId}`} className="flex items-start justify-between gap-3 border-b border-surface-200 px-3 py-2 last:border-b-0 dark:border-surface-700">
              <div className="min-w-0">
                <p className="truncate text-xs font-semibold text-surface-900 dark:text-surface-100">{recordName(record)}</p>
                <p className="mt-0.5 text-[0.68rem] text-surface-500 dark:text-surface-400">
                  {record.direction === 'inbound' ? 'Inbound' : 'Outbound'} · ID {record.providerRecordId}
                  {record.carrierName ? ` · ${record.carrierName}` : ''}
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[0.65rem] font-semibold capitalize text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                {record.outcome}
              </span>
            </div>
          ))}
          {audit.hasMoreRecords && (
            <p className="px-3 py-2 text-[0.68rem] text-surface-500">More saved records are available.</p>
          )}
        </div>
      )}

      {error && (
        <div role="alert" className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {error}
        </div>
      )}

      {!reviewPrepared && actionableCount === 0 && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300">
          <CheckCircle2 className="h-4 w-4" /> No Carrier classification changes need review.
        </div>
      )}

      {!reviewPrepared && actionableCount > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 dark:border-amber-900 dark:bg-amber-950/20">
          <div>
            <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">{actionableCount} changes require review</p>
            <p className="mt-1 text-[0.68rem] text-amber-800 dark:text-amber-300">Preparing review updates only the draft catalog. Carrier Folders remain unchanged.</p>
          </div>
          <button
            type="button"
            disabled={disabled || preparing || audit.counts.conflict > 0}
            onClick={() => void prepareReview()}
            className="flex h-8 items-center gap-1.5 rounded-lg bg-amber-500 px-3 text-xs font-semibold text-surface-950 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50"
            title={audit.counts.conflict > 0 ? 'Resolve provider conflicts and run the audit again.' : undefined}
          >
            {preparing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Prepare Organizer review
          </button>
        </div>
      )}

      {reviewPrepared && (
        <div className="mt-4">
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <span>The audit evidence is ready in the Organizer. Assign or ignore every new trunk, review changed Carriers, save, then publish folders for final approval.</span>
          </div>
          <CarrierMappingDrafts
            disabled={disabled}
            defaultOpen
            defaultOpenKey={`${surfaceKey}:${audit.runId}`}
          />
        </div>
      )}
    </section>
  );
}
